import { z } from "zod";

export const WORKLOAD_DIAGNOSIS_SCHEMA_VERSION =
  "workload-diagnosis/0.1" as const;

export const SECRET_PATTERN =
  /(?:\b(?:api[_-]?key|authorization|cookie|access[_-]?token|refresh[_-]?token|client[_-]?secret|password|secret|session|token)\s*[:=]\s*\S+|\bbearer\s+[a-z0-9._~+/=-]{8,}|\b(?:oxk_[a-z0-9_-]{8,}|sk-(?:live|test|proj)?[_-]?[a-z0-9_-]{8,}|[sr]k_(?:live|test)_[a-z0-9_-]{8,}|gh[pousr]_[a-z0-9]{8,}|github_pat_[a-z0-9_]{8,}|glpat-[a-z0-9_-]{16,}|xox[baprs]-[a-z0-9-]{8,}|npm_[a-z0-9]{8,}|sntrys_[a-z0-9_-]{8,}|whsec_[a-z0-9_-]{8,}|SG\.[a-z0-9_-]{16,}\.[a-z0-9_-]{16,}|pypi-[a-z0-9_-]{24,}|dop_v1_[a-f0-9]{16,}|hf_[a-z0-9]{20,}|A(?:KI|SI)A[A-Z0-9]{16}|AIza[a-z0-9_-]{20,})\b|\beyJ[a-z0-9_-]{8,}\.eyJ[a-z0-9_-]{8,}\.[a-z0-9_-]{8,}|[a-z][a-z0-9+.-]*:\/\/[^\s/@:]+:[^\s/@]+@|-----BEGIN [A-Z ]*PRIVATE KEY-----)/i;
const BASIC_AUTH_SECRET_PATTERN =
  /\bbasic\s+(?:[a-z0-9+/]{4})*(?:[a-z0-9+/]{4}|[a-z0-9+/]{2}==|[a-z0-9+/]{3}=)(?=$|[^a-z0-9+/=])/i;

export function containsCredentialPattern(value: string): boolean {
  return SECRET_PATTERN.test(value) || BASIC_AUTH_SECRET_PATTERN.test(value);
}

const SafeSummarySchema = z
  .string()
  .trim()
  .min(1)
  .max(500)
  .refine(
    (value) => !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value),
    { message: "Control characters are not allowed" }
  )
  .refine((value) => !containsCredentialPattern(value), {
    message: "Do not include credentials, tokens, passwords, or private keys",
  });

const SafeResponseText = (max: number) =>
  z
    .string()
    .min(1)
    .max(max)
    .refine((value) => !/[\u0000-\u001f\u007f-\u009f]/.test(value), {
      message: "Control characters are not allowed",
    });

export const WorkloadTimeHorizonSchema = z.enum([
  "single_turn",
  "single_session",
  "multi_day",
  "recurring",
  "continuous",
]);

export const WorkloadCoordinationSchema = z.enum([
  "none",
  "handoff",
  "parallel",
  "hierarchical",
]);

export const WorkloadSystemCategorySchema = z.enum([
  "code_repository",
  "issue_tracker",
  "document_store",
  "messaging",
  "crm",
  "database",
  "cloud_runtime",
  "finance",
  "browser",
  "local_files",
  "identity",
  "other",
]);

export const WorkloadAccessModeSchema = z.enum(["read", "write", "admin"]);
export const WorkloadSideEffectSchema = z.enum([
  "none",
  "reversible",
  "external",
  "irreversible",
]);
export const WorkloadDataClassSchema = z.enum([
  "public",
  "internal",
  "confidential",
  "regulated",
]);

export const WorkloadActionSchema = z.enum([
  "research",
  "draft",
  "read_internal_data",
  "write_internal_records",
  "modify_code",
  "merge_or_deploy",
  "send_external_message",
  "spend_funds",
  "create_account",
  "change_permissions",
  "delete_data",
]);

export const WorkloadApprovalPolicySchema = z.enum([
  "not_applicable",
  "per_action",
  "sensitive_actions",
  "exceptions_only",
  "undefined",
]);

export const WorkloadBudgetControlSchema = z.enum([
  "not_applicable",
  "fixed_limit",
  "dynamic_limit",
  "unbounded",
  "undefined",
]);

const SystemAccessSchema = z
  .object({
    category: WorkloadSystemCategorySchema,
    access: WorkloadAccessModeSchema,
    side_effect: WorkloadSideEffectSchema,
    data_class: WorkloadDataClassSchema,
  })
  .strict();

const AuthoritySchema = z
  .object({
    actions: z
      .array(WorkloadActionSchema)
      .max(11)
      .superRefine((actions, context) => {
        if (new Set(actions).size !== actions.length) {
          context.addIssue({
            code: "custom",
            message: "Authority actions must be unique",
          });
        }
      }),
    approval_policy: WorkloadApprovalPolicySchema,
    budget_control: WorkloadBudgetControlSchema,
  })
  .strict()
  .superRefine((authority, context) => {
    const mutating = authority.actions.some(
      (action) => !["research", "draft", "read_internal_data"].includes(action)
    );
    const spends = authority.actions.includes("spend_funds");
    if (mutating && authority.approval_policy === "not_applicable") {
      context.addIssue({
        code: "custom",
        path: ["approval_policy"],
        message: "Mutating actions require an approval policy or undefined",
      });
    }
    if (spends && authority.budget_control === "not_applicable") {
      context.addIssue({
        code: "custom",
        path: ["budget_control"],
        message: "Spending requires a budget control or undefined",
      });
    }
    if (
      !spends &&
      !["not_applicable", "undefined"].includes(authority.budget_control)
    ) {
      context.addIssue({
        code: "custom",
        path: ["budget_control"],
        message: "Budget controls are only valid when spending is in scope",
      });
    }
  });

const AccountabilitySchema = z
  .object({
    evidence: z.enum(["none", "activity_log", "artifact", "verified_outcome"]),
    acceptance: z.enum(["none", "agent", "human", "downstream_system"]),
    consequence: z.enum(["low", "moderate", "high", "regulated"]),
    retention: z.enum(["none", "short_term", "long_term", "regulated"]),
  })
  .strict();

export const WorkloadDiagnosisRequestSchema = z
  .object({
    schema_version: z.literal(WORKLOAD_DIAGNOSIS_SCHEMA_VERSION),
    workload: z
      .object({
        name: SafeSummarySchema.max(120),
        outcome: SafeSummarySchema,
        time_horizon: WorkloadTimeHorizonSchema,
        agent_count: z.number().int().min(1).max(64),
        coordination: WorkloadCoordinationSchema,
        systems: z
          .array(SystemAccessSchema)
          .max(12)
          .superRefine((systems, context) => {
            const seen = new Set<string>();
            systems.forEach((system, index) => {
              if (seen.has(system.category)) {
                context.addIssue({
                  code: "custom",
                  path: [index, "category"],
                  message: "System categories must be unique",
                });
              }
              seen.add(system.category);
            });
          }),
        authority: AuthoritySchema,
        accountability: AccountabilitySchema,
      })
      .strict(),
  })
  .strict()
  .superRefine((input, context) => {
    const { workload } = input;
    if (workload.agent_count === 1 && workload.coordination !== "none") {
      context.addIssue({
        code: "custom",
        path: ["workload", "coordination"],
        message: "One agent cannot use an agent-to-agent coordination mode",
      });
    }
    const writable = workload.systems.filter((system) => system.access !== "read");
    const compatible = (action: z.infer<typeof WorkloadActionSchema>) => {
      switch (action) {
        case "research":
        case "draft":
        case "read_internal_data":
          return true;
        case "write_internal_records":
          return writable.length > 0;
        case "modify_code":
          return writable.some((system) =>
            ["code_repository", "local_files"].includes(system.category)
          );
        case "merge_or_deploy":
          return writable.some((system) =>
            ["code_repository", "cloud_runtime"].includes(system.category)
          );
        case "send_external_message":
          return writable.some((system) =>
            ["messaging", "crm", "browser", "other"].includes(system.category)
          );
        case "spend_funds":
          return writable.some((system) =>
            ["finance", "browser"].includes(system.category)
          );
        case "create_account":
          return writable.some((system) =>
            ["identity", "browser", "other"].includes(system.category)
          );
        case "change_permissions":
          return workload.systems.some((system) => system.access === "admin");
        case "delete_data":
          return writable.some((system) =>
            [
              "code_repository",
              "document_store",
              "crm",
              "database",
              "cloud_runtime",
              "local_files",
              "other",
            ].includes(system.category)
          );
      }
    };
    workload.authority.actions.forEach((action, index) => {
      if (!compatible(action)) {
        context.addIssue({
          code: "custom",
          path: ["workload", "authority", "actions", index],
          message: `${action} requires a compatible target system and access`,
        });
      }
    });
  });

const BoundaryNameSchema = z.enum([
  "time",
  "agents",
  "systems",
  "authority",
  "accountability",
]);

const BoundaryFindingSchema = z
  .object({
    state: z.enum(["absent", "present", "critical"]),
    score: z.number().int().min(0).max(2),
    reason: SafeResponseText(300),
  })
  .strict();

const ProposedResourceSchema = z
  .object({
    resource: SafeResponseText(80),
    requested_access: WorkloadAccessModeSchema,
    scope_intents: z.array(SafeResponseText(80)).min(1).max(12),
    purpose: SafeResponseText(300),
    credential_input_required: z.literal(false),
  })
  .strict();

const HumanApprovalSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9_-]{1,80}$/),
    owner_role: z.enum([
      "workload_owner",
      "system_owner",
      "code_owner",
      "communications_owner",
      "budget_owner",
      "identity_admin",
      "data_owner",
    ]),
    timing: z.enum([
      "before_installation",
      "before_first_use",
      "per_action",
      "when_threshold_exceeded",
    ]),
    decision: SafeResponseText(300),
    scope: z.array(SafeResponseText(100)).min(1).max(16),
  })
  .strict();

export const WorkloadDiagnosisResponseSchema = z
  .object({
    schema_version: z.literal(WORKLOAD_DIAGNOSIS_SCHEMA_VERSION),
    diagnosis_id: z.string().regex(/^wdg_[a-f0-9]{24}$/),
    recommendation: z
      .object({
        verdict: z.enum(["needed", "conditional", "not_needed"]),
        mode: z.enum(["none", "receipt_only", "governed_workspace"]),
        summary: SafeResponseText(400),
        rationale: z.array(SafeResponseText(300)).max(5),
        active_boundary_count: z.number().int().min(0).max(5),
      })
      .strict(),
    boundaries: z
      .object({
        time: BoundaryFindingSchema,
        agents: BoundaryFindingSchema,
        systems: BoundaryFindingSchema,
        authority: BoundaryFindingSchema,
        accountability: BoundaryFindingSchema,
      })
      .strict(),
    missing_capabilities: z
      .array(
        z
          .object({
            id: z.string().regex(/^[a-z0-9_]{1,60}$/),
            boundary: BoundaryNameSchema,
            reason: SafeResponseText(300),
          })
          .strict()
      )
      .max(15),
    proposed_resources: z.array(ProposedResourceSchema).max(13),
    what_remains_local: z
      .array(
        z
          .object({
            item: SafeResponseText(100),
            reason: SafeResponseText(300),
          })
          .strict()
      )
      .min(1)
      .max(6),
    risks: z
      .array(
        z
          .object({
            id: z.string().regex(/^[a-z0-9_]{1,60}$/),
            severity: z.enum(["low", "medium", "high", "critical"]),
            boundary: BoundaryNameSchema,
            description: SafeResponseText(300),
            mitigation: SafeResponseText(300),
          })
          .strict()
      )
      .max(15),
    capabilities_after_installation: z.array(SafeResponseText(300)).max(10),
    human_approvals: z.array(HumanApprovalSchema).max(32),
    approval_handoff: z
      .object({
        kind: z.enum(["none", "browser_review"]),
        url: z.string().url().nullable(),
        mutates_state: z.literal(false),
        carries_sensitive_data: z.boolean(),
        approval_ids: z
          .array(z.string().regex(/^[a-z0-9_-]{1,80}$/))
          .max(32),
      })
      .strict(),
  })
  .strict()
  .superRefine((response, context) => {
    const activeCount = Object.values(response.boundaries).filter(
      (finding) => finding.state !== "absent"
    ).length;
    if (response.recommendation.active_boundary_count !== activeCount) {
      context.addIssue({
        code: "custom",
        path: ["recommendation", "active_boundary_count"],
        message: "Active boundary count does not match findings",
      });
    }

    const approvalIds = response.human_approvals.map((approval) => approval.id);
    if (
      new Set(approvalIds).size !== approvalIds.length ||
      response.approval_handoff.approval_ids.length !== approvalIds.length ||
      response.approval_handoff.approval_ids.some(
        (id, index) => id !== approvalIds[index]
      )
    ) {
      context.addIssue({
        code: "custom",
        path: ["approval_handoff", "approval_ids"],
        message: "Approval handoff IDs must match human approvals",
      });
    }

    const hasHandoff = response.approval_handoff.kind === "browser_review";
    if (
      hasHandoff !== (response.approval_handoff.url !== null) ||
      hasHandoff !== response.approval_handoff.carries_sensitive_data ||
      hasHandoff !== (response.human_approvals.length > 0)
    ) {
      context.addIssue({
        code: "custom",
        path: ["approval_handoff"],
        message: "Approval handoff fields are inconsistent",
      });
    }
  });

export type WorkloadDiagnosisRequest = z.infer<
  typeof WorkloadDiagnosisRequestSchema
>;
export type WorkloadDiagnosisResponse = z.infer<
  typeof WorkloadDiagnosisResponseSchema
>;
